import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { useAuthStore } from "../store";

type Category = { id: string; name: string; description: string; createdAt: string; updatedAt: string };
type Product = {
  id: string;
  name: string;
  categoryId: string | null;
  category: string;
  quantity: number;
  unitCost: number;
  createdAt: string;
  updatedAt: string;
};
type FormState = { name: string; categoryId: string; availableStock: string; unitCost: string };

const emptyForm: FormState = { name: "", categoryId: "", availableStock: "0", unitCost: "0" };
const formatCurrency = (cents: number) => new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(cents / 100);

export function ProductPage() {
  const token = useAuthStore((state) => state.token);
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const products = useQuery({ queryKey: ["products"], queryFn: () => api<Product[]>("/api/v1/products", {}, token || undefined) });
  const categories = useQuery({ queryKey: ["categories"], queryFn: () => api<Category[]>("/api/v1/categories", {}, token || undefined) });

  const save = useMutation({
    mutationFn: (payload: FormState) => {
      // The internal SKU is never supplied by the staff UI: the server
      // generates it on creation and edits can never change it.
      const body = editingId
        ? { name: payload.name.trim(), categoryId: payload.categoryId, unitCost: Number(payload.unitCost) }
        : { name: payload.name.trim(), categoryId: payload.categoryId, availableStock: Number(payload.availableStock), unitCost: Number(payload.unitCost) };
      return editingId
        ? api<Product>(`/api/v1/products/${editingId}`, { method: "PUT", body: JSON.stringify(body) }, token || undefined)
        : api<Product>("/api/v1/products", { method: "POST", body: JSON.stringify(body) }, token || undefined);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      setError("");
    },
    onError: (requestError: Error) => setError(requestError.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api<{ deleted: boolean }>(`/api/v1/products/${id}`, { method: "DELETE" }, token || undefined),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["products"] }),
    onError: (requestError: Error) => setError(requestError.message),
  });

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError("");
    setOpen(true);
  };

  const startEdit = (product: Product) => {
    setEditingId(product.id);
    setForm({
      name: product.name,
      categoryId: product.categoryId || "",
      availableStock: "0",
      unitCost: String(product.unitCost),
    });
    setError("");
    setOpen(true);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const availableStock = Number(form.availableStock);
    const unitCost = Number(form.unitCost);
    if (!form.name.trim() || !form.categoryId) {
      setError("Product name and category are required");
      return;
    }
    if (!Number.isInteger(availableStock) || availableStock < 0 || !Number.isInteger(unitCost) || unitCost < 0) {
      setError("Available stock and unit cost must be whole, non-negative numbers");
      return;
    }
    save.mutate(form);
  };

  return <section><div className="page-heading"><div><p className="eyebrow">04 / STOCK</p><h2>Products</h2><p className="subhead">Maintain the product catalog and opening stock behind every inventory movement.</p></div><button className="primary-button" onClick={startCreate}>Add Product</button></div>
    {open && <div className="panel form-panel"><div className="table-head"><h3>{editingId ? "Edit product" : "New product"}</h3><button className="ghost-button" type="button" onClick={() => setOpen(false)}>Cancel</button></div><form className="crud-form" onSubmit={submit}><label>Product name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></label><label>Category<select value={form.categoryId} onChange={(event) => setForm({ ...form, categoryId: event.target.value })} required><option value="" disabled>Select a category</option>{categories.data?.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}</select></label>{!editingId && <label>Available stock<input type="number" min="0" step="1" value={form.availableStock} onChange={(event) => setForm({ ...form, availableStock: event.target.value })} required /></label>}<label>Unit cost (centavos)<input type="number" min="0" step="1" value={form.unitCost} onChange={(event) => setForm({ ...form, unitCost: event.target.value })} required /></label>{categories.isError && <div className="notice">{(categories.error as Error).message}</div>}{!categories.isLoading && !categories.data?.length && <div className="notice">Create a category before adding a product.</div>}{error && <div className="notice">{error}</div>}<button className="primary-button" type="submit" disabled={save.isPending || !categories.data?.length}>{save.isPending ? "Saving…" : editingId ? "Save changes" : "Create product"}</button></form></div>}
    {products.isError && <div className="notice">{(products.error as Error).message}</div>}
    <div className="panel table-panel"><div className="table-head"><h3>Product catalog</h3><span className="tag">{products.data?.length || 0} RECORDS</span></div>{products.isLoading ? <div className="module-empty"><p>Loading products…</p></div> : products.data?.length ? <div className="record-list">{products.data.map((product) => <div className="record-row" key={product.id}><div><strong>{product.name}</strong><span>{product.category}</span></div><div><strong>Available stock: {product.quantity}</strong><span>{formatCurrency(product.unitCost)}</span></div><div className="record-actions"><button className="ghost-button" onClick={() => startEdit(product)}>Edit</button><button className="ghost-button danger" onClick={() => { if (window.confirm(`Delete ${product.name}? Products with movement history cannot be deleted.`)) remove.mutate(product.id); }}>Delete</button></div></div>)}</div> : <div className="module-empty"><p>No products have been added yet.</p><button className="ghost-button" onClick={startCreate}>Create first product</button></div>}</div>
  </section>;
}
